import moment from 'moment';
import {getMonthRange, getNextMonthRange, getPreviousMonthRange} from '../../forecast-app/shared/util/DateUtil';
import {ReportDefinitionV1} from './contracts/report-definition.types';

export type DatePresetToken = NonNullable<ReportDefinitionV1['datePreset']>;

export interface RelativeDatePreset {
	token: DatePresetToken;
	labelId: string;
	resolve(now: Date): {startDate: string; endDate: string};
}

function toISODate(date: Date): string {
	return moment(date).format('YYYY-MM-DD');
}

function toISORange(range: {startDate: Date; endDate: Date}): {startDate: string; endDate: string} {
	return {startDate: toISODate(range.startDate), endDate: toISODate(range.endDate)};
}

/** Normalizes `now` to the 1st of its own month before handing it to
 * `getNextMonthRange`/`getPreviousMonthRange`, which shift months via `Date.setMonth()`
 * on a clone of the given date — an operation that overflows into the following month when
 * the anchor day (29/30/31) doesn't exist in the target month. Every month has a day 1, so
 * this sidesteps the overflow while still delegating to those functions for the range math. */
function startOfCurrentMonth(now: Date): Date {
	return new Date(now.getFullYear(), now.getMonth(), 1);
}

/** Calendar quarter (Jan-Mar, Apr-Jun, Jul-Sep, Oct-Dec) containing `now`, shifted by
 * `quarterOffset` quarters. No fiscal-quarter concept exists anywhere in this codebase. */
function quarterRange(now: Date, quarterOffset: number): {startDate: string; endDate: string} {
	const quarterIndex = Math.floor(now.getMonth() / 3) + quarterOffset;
	const year = now.getFullYear() + Math.floor(quarterIndex / 4);
	const normalizedQuarterIndex = ((quarterIndex % 4) + 4) % 4;
	const startMonth = normalizedQuarterIndex * 3;
	const start = new Date(year, startMonth, 1);
	const end = new Date(year, startMonth + 3, 0);
	return {startDate: toISODate(start), endDate: toISODate(end)};
}

/** Locale-driven, matching how weeks already render elsewhere in the app (e.g. the scheduling
 * timeline): Sunday-start for the 'en' locale, Monday-start otherwise, via moment's own
 * locale-aware startOf/endOf('week'). Deliberately NOT DateUtil.getWeekDateRangeFromDate: this
 * uses the app's moment locale directly (set in `containers/root.js` alongside date-fns's own
 * default locale) rather than depending on DateUtil.getWeekDateRangeFromDate's app-wide date-fns
 * default being configured. */
function weekRange(now: Date, weekOffset: number): {startDate: string; endDate: string} {
	const shifted = moment(now).add(weekOffset, 'weeks');
	return {
		startDate: shifted.clone().startOf('week').format('YYYY-MM-DD'),
		endDate: shifted.clone().endOf('week').format('YYYY-MM-DD'),
	};
}

// Keyed by every member of DatePresetToken so omitting one is a compile error, rather than a
// dropdown option that silently never renders.
const PRESET_DEFINITIONS: Record<DatePresetToken, Omit<RelativeDatePreset, 'token'>> = {
	next_quarter: {
		labelId: 'ai_report_builder.date_preset.next_quarter',
		resolve: now => quarterRange(now, 1),
	},
	this_quarter: {
		labelId: 'ai_report_builder.date_preset.this_quarter',
		resolve: now => quarterRange(now, 0),
	},
	last_quarter: {
		labelId: 'ai_report_builder.date_preset.last_quarter',
		resolve: now => quarterRange(now, -1),
	},
	next_month: {
		labelId: 'ai_report_builder.date_preset.next_month',
		resolve: now => toISORange(getNextMonthRange(startOfCurrentMonth(now))),
	},
	this_month: {
		labelId: 'ai_report_builder.date_preset.this_month',
		resolve: now => toISORange(getMonthRange(now)),
	},
	last_month: {
		labelId: 'ai_report_builder.date_preset.last_month',
		resolve: now => toISORange(getPreviousMonthRange(startOfCurrentMonth(now))),
	},
	next_week: {
		labelId: 'ai_report_builder.date_preset.next_week',
		resolve: now => weekRange(now, 1),
	},
	this_week: {
		labelId: 'ai_report_builder.date_preset.this_week',
		resolve: now => weekRange(now, 0),
	},
	last_week: {
		labelId: 'ai_report_builder.date_preset.last_week',
		resolve: now => weekRange(now, -1),
	},
};

// Controls both render and test order; unchanged from the original array literal's ordering.
const DISPLAY_ORDER: DatePresetToken[] = [
	'next_quarter',
	'this_quarter',
	'last_quarter',
	'next_month',
	'this_month',
	'last_month',
	'next_week',
	'this_week',
	'last_week',
];

export const RELATIVE_DATE_PRESETS: RelativeDatePreset[] = DISPLAY_ORDER.map(token => ({
	token,
	...PRESET_DEFINITIONS[token],
}));

export function findRelativeDatePreset(token: string | null | undefined): RelativeDatePreset | undefined {
	return RELATIVE_DATE_PRESETS.find(preset => preset.token === token);
}
